import axios from "axios";
import { ADMIN_API } from "../../config";

/**
 * Renvoie la liste des nomenclatures par spécialité.
 * @return Array
 */
function fetchNomenclature(speciality) {
  return axios
    .get(ADMIN_API + "/nomenclature/" + speciality)
    .then((response) => response.data);
}

/**
 * Crée une nouvelle nomenclature.
 * @param {Object} nomenclatureData - L'objet contenant les données de la nomenclature à créer.
 * @param {string} nomenclatureData.speciality - La spécialité de la nomenclature.
 * @param {string} nomenclatureData.codeAmbulant - Le code ambulant de la nomenclature.
 * @param {string} nomenclatureData.codeHospitalisation - Le code d'hospitalisation de la nomenclature.
 * @param {string} nomenclatureData.n - Le numéro ou identifiant de la nomenclature.
 * @param {string} nomenclatureData.name - Le nom de la nomenclature.
 * @param {string} nomenclatureData.type - Le type de la nomenclature.
 * @param {string} nomenclatureData.subType - Le sous-type de la nomenclature.
 * @return {Promise<Object>} - Une promesse qui résout les données de la réponse.
 */
function createNomenclature(nomenclatureData) {
  return axios
    .post(ADMIN_API + "/nomenclature", nomenclatureData)
    .then((response) => response.data);
}

/**
 * Met à jour une nomenclature.
 * @param {Object} speciality - L'objet contenant les données de la nomenclature à mettre à jour.
 * @return {Promise<Array>} - Une promesse qui résout les données de la réponse.
 */
function updateNomenclature(speciality) {
  return axios
    .put(ADMIN_API + "/nomenclature", speciality)
    .then((response) => response.data);
}

/**
 * Met à jour le type d'une série de nomenclatures.
 * @param {Object} data - Objet contenant un tableau d'IDs et la nouvelle valeur du type (1 ou 2).
 * @return {Promise<Object>} - Une promesse qui résout les données de la réponse.
 */
function updateNomenclatureType(data) {
  return axios
    .put(ADMIN_API + "/nomenclature/update-type", data)
    .then((response) => response.data);
}

/**
 * Met à jour le sous-type d'une série de nomenclatures.
 * @param {Object} data - Objet contenant un tableau d'IDs et la nouvelle valeur de subType.
 * @return {Promise<Object>} - Une promesse qui résout les données de la réponse.
 */
function updateNomenclatureSubType(data) {
  return axios
    .put(ADMIN_API + "/nomenclature/update-subtype", data)
    .then((response) => response.data);
}

/**
 * Récupère la liste des campagnes marketing.
 */
function fetchMarketing() {
  return axios
    .get(ADMIN_API + "/marketing")
    .then((response) => response.data);
}

/**
 * Crée une nouvelle campagne marketing.
 */
function createMarketingCampaign(data) {
  return axios
    .post(ADMIN_API + "/marketing", data)
    .then((response) => response.data);
}

/**
 * Met à jour le statut d'une campagne marketing.
 * @param {number} id - L'ID de la campagne marketing.
 * @param {string} status - Le nouveau statut de la campagne ("active" ou "inactive").
 */
function updateMarketingStatus(id, status) {
  return axios
    .put(`${ADMIN_API}/marketing/${id}/status`, { status })
    .then((response) => response.data);
}

/**
 * Récupère les données d'une campagne marketing spécifique.
 * 
 * @param {string|number} id - L'identifiant unique de la campagne marketing à récupérer.
 * @returns {Promise<Object>} - Une promesse qui se résout avec les données de la campagne marketing.
 */
function getMarketingCampaign(id) {
  return axios
    .get(`${ADMIN_API}/marketing/${id}`)
    .then((response) => response.data);
}

/**
 * Met à jour les données d'une campagne marketing existante.
 * 
 * @param {Object} data - Les données de la campagne marketing à mettre à jour, incluant son identifiant et les champs à modifier.
 * @param {string|number} id - L'identifiant unique de la campagne marketing.
 */
function updateMarketingCampaign(id, data) {
  return axios
    .put(`${ADMIN_API}/marketing/${id}`, data)
    .then((response) => response.data);
}

/**
 * Supprime une campagne marketing.
 * 
 */
function deleteMarketingCampaign(id) {
  return axios
    .delete(`${ADMIN_API}/marketing/${id}`)
    .then((response) => response.data);
}

// Assignez l'objet à une variable avant de l'exporter
const api = {
  createNomenclature,
  fetchNomenclature,
  updateNomenclature,
  updateNomenclatureType,
  updateNomenclatureSubType,
  fetchMarketing,
  createMarketingCampaign,
  updateMarketingStatus,
  getMarketingCampaign,
  updateMarketingCampaign,
  deleteMarketingCampaign
};

export default api;
